import React, { useState } from 'react'
import { Card, Button, Badge, Spinner, useLocale } from '@betina/ui'
import { useQuery, useMutation } from '../hooks/useApi.js'
import { householdApi, identityApi, setUserId, clearUserId, currentUserId } from '../lib/api.js'

const COMMON_ALLERGENS = ['gluten', 'lactose', 'arachide', 'œuf', 'fruits à coque', 'soja', 'poisson', 'crustacés']
const COMMON_PREFS = ['végétarien', 'végan', 'halal', 'sans porc', 'sans gluten', 'sans lactose']

export function Household() {
  const { t } = useLocale()
  const [userId, setUserIdState] = useState(currentUserId())
  const { data: members, loading, refetch } = useQuery(() => householdApi.get(), [userId])
  const [showAdd, setShowAdd] = useState(false)
  const [editing, setEditing] = useState(null)

  function handleLinked(householdId) {
    setUserId(householdId)
    setUserIdState(householdId)
  }

  function handleSwitch() {
    if (!confirm('Se déconnecter de ce foyer ?')) return
    clearUserId()
    setUserIdState(null)
  }

  const { mutate: addMember } = useMutation((m) => householdApi.addMember(m))
  const { mutate: updateMember } = useMutation(({ id, fields }) => householdApi.updateMember(id, fields))
  const { mutate: removeMember } = useMutation((id) => householdApi.removeMember(id))

  async function handleAdd(member) {
    await addMember(member)
    setShowAdd(false)
    refetch()
  }

  async function handleUpdate(id, fields) {
    await updateMember({ id, fields })
    setEditing(null)
    refetch()
  }

  async function handleRemove(member) {
    if (!confirm(`Supprimer ${member.name} ?`)) return
    await removeMember(member.id)
    refetch()
  }

  const showLinkPanel = !userId || (!loading && !members?.length)

  return (
    <div className="p-4 space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-bold text-gray-800">{t('household')}</h1>
        <Button size="sm" onClick={() => setShowAdd(true)}>
          + {t('add_member')}
        </Button>
      </div>

      {showLinkPanel && <LinkWhatsAppPanel onLinked={handleLinked} />}

      {loading ? (
        <div className="flex justify-center py-10"><Spinner /></div>
      ) : !members?.length ? (
        <Card className="text-center py-8">
          <p className="text-gray-400 text-sm">{t('no_members')}</p>
          <Button size="sm" className="mt-3" onClick={() => setShowAdd(true)}>
            + {t('add_member')}
          </Button>
        </Card>
      ) : (
        <div className="space-y-2">
          {members.map((m) => (
            <MemberCard
              key={m.id}
              member={m}
              onEdit={() => setEditing(m)}
              onDelete={() => handleRemove(m)}
            />
          ))}
        </div>
      )}

      {userId && (
        <div className="flex items-center justify-between text-xs text-gray-400 pt-2">
          <span>Foyer actif : <code className="text-gray-500">{userId}</code></span>
          <button onClick={handleSwitch} className="hover:text-gray-600 underline">
            Changer de foyer
          </button>
        </div>
      )}

      {(showAdd || editing) && (
        <MemberForm
          member={editing}
          onCancel={() => { setShowAdd(false); setEditing(null) }}
          onSubmit={editing ? (f) => handleUpdate(editing.id, f) : handleAdd}
        />
      )}
    </div>
  )
}

function LinkWhatsAppPanel({ onLinked }) {
  const [phone, setPhone] = useState('')
  const [error, setError] = useState(null)
  const [busy, setBusy] = useState(false)

  async function submit(e) {
    e?.preventDefault?.()
    if (!phone.trim()) return
    setBusy(true)
    setError(null)
    try {
      const { householdId } = await identityApi.identify(phone.trim())
      onLinked(householdId)
    } catch (err) {
      const status = err?.response?.status
      if (status === 404) {
        setError('Aucun foyer trouvé pour ce numéro. As-tu bien démarré la conversation avec le bot WhatsApp ?')
      } else {
        setError('Erreur lors de la résolution. Vérifie le format du numéro.')
      }
    } finally {
      setBusy(false)
    }
  }

  return (
    <Card className="bg-emerald-50/40 border border-emerald-100">
      <p className="font-semibold text-gray-800 text-sm">Lier mon foyer WhatsApp</p>
      <p className="text-xs text-gray-500 mt-1">
        Saisis le numéro WhatsApp utilisé pour discuter avec le bot (international, sans <code>+</code>, ex. <code>32479123456</code>).
      </p>
      <form onSubmit={submit} className="mt-3 flex gap-2">
        <input
          type="tel"
          inputMode="numeric"
          autoComplete="tel"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          placeholder="32479123456"
          className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400"
        />
        <Button size="sm" type="submit" disabled={busy || !phone.trim()}>
          {busy ? '…' : 'Identifier'}
        </Button>
      </form>
      {error && <p className="text-xs text-red-600 mt-2">{error}</p>}
    </Card>
  )
}

function MemberCard({ member, onEdit, onDelete }) {
  return (
    <Card>
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <span className="font-semibold text-gray-800">{member.name}</span>
            {member.age != null && <span className="text-xs text-gray-400">{member.age} ans</span>}
          </div>
          {member.allergies?.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1">
              {member.allergies.map((a) => (
                <Badge key={a} color="red">⚠️ {a}</Badge>
              ))}
            </div>
          )}
          {member.preferences?.length > 0 && (
            <div className="mt-1 flex flex-wrap gap-1">
              {member.preferences.map((p) => (
                <Badge key={p} color="green">{p}</Badge>
              ))}
            </div>
          )}
        </div>
        <div className="flex gap-1">
          <button onClick={onEdit} className="text-gray-400 hover:text-gray-600 text-sm px-2 py-1">✏️</button>
          <button onClick={onDelete} className="text-gray-400 hover:text-red-500 text-sm px-2 py-1">🗑️</button>
        </div>
      </div>
    </Card>
  )
}

function MemberForm({ member, onSubmit, onCancel }) {
  const { t } = useLocale()
  const [name, setName] = useState(member?.name ?? '')
  const [age, setAge] = useState(member?.age ?? '')
  const [allergies, setAllergies] = useState(member?.allergies ?? [])
  const [preferences, setPreferences] = useState(member?.preferences ?? [])

  function toggle(set, value, setter) {
    setter(set.includes(value) ? set.filter((v) => v !== value) : [...set, value])
  }

  function submit() {
    if (!name.trim()) return
    onSubmit({
      name: name.trim(),
      age: age === '' ? null : parseInt(age, 10),
      allergies,
      preferences,
    })
  }

  return (
    <div className="fixed inset-0 bg-black/40 flex items-end justify-center z-50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-lg p-4 space-y-3 max-h-[90vh] overflow-y-auto">
        <p className="font-semibold text-gray-800">
          {member ? `Modifier ${member.name}` : t('add_member')}
        </p>

        <div>
          <label className="text-xs text-gray-500">{t('member_name')}</label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Prénom…"
            className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400"
            autoFocus
          />
        </div>

        <div>
          <label className="text-xs text-gray-500">{t('member_age')}</label>
          <input
            type="number"
            value={age}
            onChange={(e) => setAge(e.target.value)}
            placeholder="Âge (optionnel)"
            className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400"
            min="0"
            max="120"
          />
        </div>

        <div>
          <p className="text-xs text-gray-500 mb-1">{t('member_allergies')}</p>
          <div className="flex flex-wrap gap-1.5">
            {COMMON_ALLERGENS.map((a) => (
              <button
                key={a}
                type="button"
                onClick={() => toggle(allergies, a, setAllergies)}
                className={`text-xs px-2 py-1 rounded-full border transition-colors ${
                  allergies.includes(a)
                    ? 'bg-red-50 border-red-300 text-red-700'
                    : 'border-gray-200 text-gray-500 hover:border-gray-300'
                }`}
              >
                {a}
              </button>
            ))}
          </div>
        </div>

        <div>
          <p className="text-xs text-gray-500 mb-1">{t('member_preferences')}</p>
          <div className="flex flex-wrap gap-1.5">
            {COMMON_PREFS.map((p) => (
              <button
                key={p}
                type="button"
                onClick={() => toggle(preferences, p, setPreferences)}
                className={`text-xs px-2 py-1 rounded-full border transition-colors ${
                  preferences.includes(p)
                    ? 'bg-emerald-50 border-emerald-300 text-emerald-700'
                    : 'border-gray-200 text-gray-500 hover:border-gray-300'
                }`}
              >
                {p}
              </button>
            ))}
          </div>
        </div>

        <div className="flex gap-2 pt-2">
          <Button className="flex-1 justify-center" onClick={submit}>{t('save')}</Button>
          <Button variant="secondary" className="flex-1 justify-center" onClick={onCancel}>
            {t('cancel')}
          </Button>
        </div>
      </div>
    </div>
  )
}
